import type {
  AdmissionInquiryPayload,
  AdmissionInquiryRecord,
} from "@project/types/admission-inquiry";
import {
  DEFAULT_PHONE_COUNTRY,
  isValidMobileNumber,
  type PhoneCountryCode,
} from "@project/lib/mobile-number";

export type InquiryFamilyMember = {
  rowId: string;
  relationship: string;
  relationshipCustom: boolean;
  name: string;
  mobile: string;
  mobileCountry: PhoneCountryCode;
  details: string;
};

export function createInquiryFamilyRow(): InquiryFamilyMember {
  return {
    rowId: crypto.randomUUID(),
    relationship: "",
    relationshipCustom: false,
    name: "",
    mobile: "",
    mobileCountry: DEFAULT_PHONE_COUNTRY,
    details: "",
  };
}

export function createInquiryFamilyRows(count: number): InquiryFamilyMember[] {
  return Array.from({ length: count }, () => createInquiryFamilyRow());
}

export type InquiryMedicine = {
  rowId: string;
  currentTreatmentName: string;
  treatmentNameCustom: boolean;
  name: string;
  dosage: string;
  frequency: string;
};

export function createMedicineRow(): InquiryMedicine {
  return {
    rowId: crypto.randomUUID(),
    currentTreatmentName: "",
    treatmentNameCustom: false,
    name: "",
    dosage: "",
    frequency: "",
  };
}

export function createMedicineRows(count: number): InquiryMedicine[] {
  return Array.from({ length: count }, () => createMedicineRow());
}

export type InquiryOperation = {
  rowId: string;
  operationType: string;
  operationDate: string;
  operationDoctorName: string;
  operationHospitalName: string;
  districtId: string;
  cityId: string;
  areaId: string;
  districtName: string;
  cityName: string;
  areaName: string;
  operationDetails: string;
  takesRecoveryMedicine: boolean;
  recoveryMedicineDetails: string;
};

export function createOperationRow(): InquiryOperation {
  return {
    rowId: crypto.randomUUID(),
    operationType: "",
    operationDate: "",
    operationDoctorName: "",
    operationHospitalName: "",
    districtId: "",
    cityId: "",
    areaId: "",
    districtName: "",
    cityName: "",
    areaName: "",
    operationDetails: "",
    takesRecoveryMedicine: false,
    recoveryMedicineDetails: "",
  };
}

export function createOperationRows(count: number): InquiryOperation[] {
  return Array.from({ length: count }, () => createOperationRow());
}

export type AdmissionInquiryFormState = {
  applicationDate: string;
  inquirerName: string;
  inquirerMobile: string;
  inquirerMobileCountry: PhoneCountryCode;
  inquirerEmail: string;
  referencePersonName: string;
  referencePersonMobile: string;
  referencePersonMobileCountry: PhoneCountryCode;
  pincode: string;
  landmark: string;
  elderName: string;
  elderWeight: string;
  elderPincode: string;
  elderLandmark: string;
  reasonForAdmission: string;
  pensionAmount: string;
  otherIncome: string;
  otherFinancierName: string;
  otherFinancierNumber: string;
  otherFinancierEmail: string;
  additionalNotes: string;
  physicalHealthStatus: string;
  hasPhysicalDisability: boolean;
  physicalDisabilityDetails: string;
  hasMentalHealthIssue: boolean;
  mentalHealthIssueDetails: string;
  currentDoctorName: string;
  currentHospitalName: string;
  currentHospitalAddress: string;
  currentlyUnderTreatment: boolean;
  takesCurrentMedicines: boolean;
  hadOperationBefore: boolean;
  hasExistingDisease: boolean;
  diseaseDetails: string;
  hasFoodAllergy: boolean;
  foodAllergyDetails: string;
  hasMedicineAllergy: boolean;
  medicineAllergyDetails: string;
  specialMedicalInstructions: string;
};

export function todayIso(): string {
  return new Date().toISOString().slice(0, 10);
}

export const INITIAL_INQUIRY_FORM: AdmissionInquiryFormState = {
  applicationDate: todayIso(),
  inquirerName: "",
  inquirerMobile: "",
  inquirerMobileCountry: DEFAULT_PHONE_COUNTRY,
  inquirerEmail: "",
  referencePersonName: "",
  referencePersonMobile: "",
  referencePersonMobileCountry: DEFAULT_PHONE_COUNTRY,
  pincode: "",
  landmark: "",
  elderName: "",
  elderWeight: "",
  elderPincode: "",
  elderLandmark: "",
  reasonForAdmission: "",
  pensionAmount: "",
  otherIncome: "",
  otherFinancierName: "",
  otherFinancierNumber: "",
  otherFinancierEmail: "",
  additionalNotes: "",
  physicalHealthStatus: "",
  hasPhysicalDisability: false,
  physicalDisabilityDetails: "",
  hasMentalHealthIssue: false,
  mentalHealthIssueDetails: "",
  currentDoctorName: "",
  currentHospitalName: "",
  currentHospitalAddress: "",
  currentlyUnderTreatment: false,
  takesCurrentMedicines: false,
  hadOperationBefore: false,
  hasExistingDisease: false,
  diseaseDetails: "",
  hasFoodAllergy: false,
  foodAllergyDetails: "",
  hasMedicineAllergy: false,
  medicineAllergyDetails: "",
  specialMedicalInstructions: "",
};

export function inquiryRecordToFormState(
  record: AdmissionInquiryRecord
): AdmissionInquiryFormState {
  return {
    applicationDate: record.application_date ?? todayIso(),
    inquirerName: record.inquirer_name ?? "",
    inquirerMobile: record.inquirer_mobile ?? "",
    inquirerMobileCountry: DEFAULT_PHONE_COUNTRY,
    inquirerEmail: record.inquirer_email ?? "",
    referencePersonName: record.reference_person_name ?? "",
    referencePersonMobile: record.reference_person_mobile ?? "",
    referencePersonMobileCountry: DEFAULT_PHONE_COUNTRY,
    pincode: record.pincode ?? "",
    landmark: record.landmark ?? "",
    elderName: record.elder_name ?? "",
    elderWeight:
      record.elder_weight === null || record.elder_weight === undefined
        ? ""
        : String(record.elder_weight),
    elderPincode: record.elder_pincode ?? "",
    elderLandmark: record.elder_landmark ?? "",
    reasonForAdmission: record.reason_for_admission ?? "",
    pensionAmount: record.pension_amount ?? "",
    otherIncome: record.other_income ?? "",
    otherFinancierName: record.other_financier_name ?? "",
    otherFinancierNumber: record.other_financier_number ?? "",
    otherFinancierEmail: record.other_financier_email ?? "",
    additionalNotes: record.additional_notes ?? "",
    physicalHealthStatus: record.physical_health_status ?? "",
    hasPhysicalDisability: Boolean(record.has_physical_disability),
    physicalDisabilityDetails: record.physical_disability_details ?? "",
    hasMentalHealthIssue: Boolean(record.has_mental_health_issue),
    mentalHealthIssueDetails: record.mental_health_issue_details ?? "",
    currentDoctorName: record.current_doctor_name ?? "",
    currentHospitalName: record.current_hospital_name ?? "",
    currentHospitalAddress: record.current_hospital_address ?? "",
    currentlyUnderTreatment: Boolean(record.currently_under_treatment),
    takesCurrentMedicines: Boolean(record.takes_current_medicines),
    hadOperationBefore: Boolean(record.had_operation_before),
    hasExistingDisease: Boolean(record.has_existing_disease),
    diseaseDetails: record.disease_details ?? "",
    hasFoodAllergy: Boolean(record.has_food_allergy),
    foodAllergyDetails: record.food_allergy_details ?? "",
    hasMedicineAllergy: Boolean(record.has_medicine_allergy),
    medicineAllergyDetails: record.medicine_allergy_details ?? "",
    specialMedicalInstructions: record.special_medical_instructions ?? "",
  };
}

export function inquiryRecordToMedicines(
  record: AdmissionInquiryRecord
): InquiryMedicine[] {
  const medicines = record.medicines ?? [];
  if (medicines.length === 0) {
    return createMedicineRows(1);
  }
  return medicines.map(medicine => ({
    rowId: crypto.randomUUID(),
    currentTreatmentName: medicine.current_treatment_name ?? "",
    treatmentNameCustom: false,
    name: medicine.name ?? "",
    dosage: medicine.dosage ?? "",
    frequency: medicine.frequency ?? "",
  }));
}

export function inquiryRecordToOperations(
  record: AdmissionInquiryRecord
): InquiryOperation[] {
  const operations = record.operations ?? [];
  if (operations.length === 0) {
    return createOperationRows(1);
  }
  return operations.map(operation => ({
    rowId: crypto.randomUUID(),
    operationType: operation.operation_type ?? "",
    operationDate: operation.operation_date ?? "",
    operationDoctorName: operation.operation_doctor_name ?? "",
    operationHospitalName: operation.operation_hospital_name ?? "",
    districtId:
      operation.district !== null && operation.district !== undefined
        ? String(operation.district)
        : "",
    cityId:
      operation.city !== null && operation.city !== undefined
        ? String(operation.city)
        : "",
    areaId:
      operation.area !== null && operation.area !== undefined
        ? String(operation.area)
        : "",
    districtName: operation.district_name ?? "",
    cityName: operation.city_name ?? "",
    areaName: operation.area_name ?? "",
    operationDetails: operation.operation_details ?? "",
    takesRecoveryMedicine: Boolean(operation.takes_recovery_medicine),
    recoveryMedicineDetails: operation.recovery_medicine_details ?? "",
  }));
}

export function inquiryRecordToFamilyMembers(
  record: AdmissionInquiryRecord
): InquiryFamilyMember[] {
  const members = record.family_members ?? [];
  if (members.length === 0) {
    return createInquiryFamilyRows(3);
  }
  const rows: InquiryFamilyMember[] = members.map(member => {
    const relationshipName =
      String(member.relationship_name ?? "").trim() ||
      (typeof member.relationship === "string"
        ? member.relationship.trim()
        : "");
    return {
      rowId: crypto.randomUUID(),
      relationship: relationshipName,
      relationshipCustom: false,
      name: member.name ?? "",
      mobile: member.mobile ?? "",
      mobileCountry: DEFAULT_PHONE_COUNTRY,
      details: member.details ?? "",
    };
  });
  if (rows.length < 3) {
    return [...rows, ...createInquiryFamilyRows(3 - rows.length)];
  }
  return rows;
}

export function buildInquiryPayload(
  form: AdmissionInquiryFormState,
  location: { districtId: string; cityId: string; areaId: string },
  elderLocation: { districtId: string; cityId: string; areaId: string },
  medicines: InquiryMedicine[],
  operations: InquiryOperation[],
  familyMembers: InquiryFamilyMember[]
): AdmissionInquiryPayload {
  const medicineRows = form.takesCurrentMedicines
    ? medicines
        .filter(row => row.name.trim())
        .map(row => ({
          current_treatment_name: row.currentTreatmentName.trim() || undefined,
          name: row.name.trim(),
          dosage: row.dosage.trim() || undefined,
          frequency: row.frequency.trim() || undefined,
        }))
    : [];

  const operationRows = form.hadOperationBefore
    ? operations
        .filter(
          row =>
            row.operationType.trim() ||
            row.operationDoctorName.trim() ||
            row.operationHospitalName.trim() ||
            row.operationDetails.trim()
        )
        .map(row => ({
          operation_type: row.operationType.trim() || undefined,
          operation_date: row.operationDate || null,
          operation_doctor_name: row.operationDoctorName.trim() || undefined,
          operation_hospital_name:
            row.operationHospitalName.trim() || undefined,
          district: row.districtId ? Number(row.districtId) : undefined,
          city: row.cityId ? Number(row.cityId) : undefined,
          area: row.areaId ? Number(row.areaId) : undefined,
          operation_details: row.operationDetails.trim() || undefined,
          takes_recovery_medicine: row.takesRecoveryMedicine,
          recovery_medicine_details: row.takesRecoveryMedicine
            ? row.recoveryMedicineDetails.trim() || undefined
            : undefined,
        }))
    : [];

  const family_members = familyMembers
    .filter(row => row.name.trim())
    .map(row => ({
      relationship: row.relationship.trim(),
      name: row.name.trim(),
      mobile: row.mobile.trim(),
      details: row.details.trim() || undefined,
    }));

  return {
    application_date: form.applicationDate || todayIso(),
    inquirer_name: form.inquirerName.trim(),
    inquirer_mobile: form.inquirerMobile.trim(),
    inquirer_email: form.inquirerEmail.trim(),
    reference_person_name: form.referencePersonName.trim() || undefined,
    reference_person_mobile: form.referencePersonMobile.trim() || undefined,
    district: Number(location.districtId),
    city: Number(location.cityId),
    area: Number(location.areaId),
    pincode: form.pincode.trim(),
    landmark: form.landmark.trim() || undefined,
    elder_name: form.elderName.trim(),
    elder_weight: (() => {
      const raw = form.elderWeight.trim();
      if (!raw) {
        return undefined;
      }
      const n = Number(raw);
      return Number.isFinite(n) ? n : raw;
    })(),
    elder_district: Number(elderLocation.districtId),
    elder_city: Number(elderLocation.cityId),
    elder_area: Number(elderLocation.areaId),
    elder_pincode: form.elderPincode.trim(),
    elder_landmark: form.elderLandmark.trim() || undefined,
    reason_for_admission: form.reasonForAdmission.trim(),
    other_financier_name: form.otherFinancierName.trim() || undefined,
    other_financier_number: form.otherFinancierNumber.trim() || undefined,
    other_financier_email: form.otherFinancierEmail.trim() || undefined,
    pension_amount: form.pensionAmount.trim() || undefined,
    other_income: form.otherIncome.trim() || undefined,
    additional_notes: form.additionalNotes.trim() || undefined,
    physical_health_status: form.physicalHealthStatus.trim() || undefined,
    has_physical_disability: form.hasPhysicalDisability,
    physical_disability_details: form.hasPhysicalDisability
      ? form.physicalDisabilityDetails.trim() || undefined
      : undefined,
    has_mental_health_issue: form.hasMentalHealthIssue,
    mental_health_issue_details: form.hasMentalHealthIssue
      ? form.mentalHealthIssueDetails.trim() || undefined
      : undefined,
    current_doctor_name: form.currentDoctorName.trim() || undefined,
    current_hospital_name: form.currentHospitalName.trim() || undefined,
    current_hospital_address: form.currentHospitalAddress.trim() || undefined,
    currently_under_treatment: form.currentlyUnderTreatment,
    takes_current_medicines: form.takesCurrentMedicines,
    medicines: medicineRows,
    had_operation_before: form.hadOperationBefore,
    operations: operationRows,
    has_existing_disease: form.hasExistingDisease,
    disease_details: form.hasExistingDisease
      ? form.diseaseDetails.trim() || undefined
      : undefined,
    has_food_allergy: form.hasFoodAllergy,
    food_allergy_details: form.hasFoodAllergy
      ? form.foodAllergyDetails.trim() || undefined
      : undefined,
    has_medicine_allergy: form.hasMedicineAllergy,
    medicine_allergy_details: form.hasMedicineAllergy
      ? form.medicineAllergyDetails.trim() || undefined
      : undefined,
    special_medical_instructions:
      form.specialMedicalInstructions.trim() || undefined,
    family_members,
  };
}

const vkey = (k: string) => `account.inquiryForm.validation.${k}`;

const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

function validateInquirerContact(
  form: AdmissionInquiryFormState
): InquiryValidationError | null {
  const mobile = form.inquirerMobile.trim();
  if (!mobile) {
    return { messageKey: vkey("inquirerMobileRequired") };
  }
  if (!isValidMobileNumber(mobile, form.inquirerMobileCountry)) {
    return { messageKey: vkey("inquirerMobileInvalid") };
  }

  const email = form.inquirerEmail.trim();
  if (!email) {
    return { messageKey: vkey("inquirerEmailRequired") };
  }
  if (!EMAIL_PATTERN.test(email)) {
    return { messageKey: vkey("inquirerEmailInvalid") };
  }
  return null;
}

function validateReferencePersonContact(
  form: AdmissionInquiryFormState
): InquiryValidationError | null {
  const mobile = form.referencePersonMobile.trim();
  if (
    mobile &&
    !isValidMobileNumber(mobile, form.referencePersonMobileCountry, {
      required: false,
    })
  ) {
    return { messageKey: vkey("referencePersonMobileInvalid") };
  }
  return null;
}

function validateElderWeight(
  form: AdmissionInquiryFormState
): InquiryValidationError | null {
  const raw = form.elderWeight.trim();
  if (!raw) {
    return null;
  }
  const n = Number(raw);
  if (!Number.isFinite(n) || n <= 0 || n > 500) {
    return { messageKey: vkey("elderWeightInvalid") };
  }
  return null;
}

const REQUIRED_FAMILY_MEMBER_COUNT = 3;

export type InquiryValidationError = {
  messageKey: string;
  params?: Record<string, string | number>;
};

function validateRequiredFamilyMembers(
  familyMembers: InquiryFamilyMember[]
): InquiryValidationError | null {
  for (let i = 0; i < REQUIRED_FAMILY_MEMBER_COUNT; i++) {
    const member = familyMembers[i];
    const number = i + 1;
    if (!member?.name.trim()) {
      return {
        messageKey: vkey("familyMemberNameRequired"),
        params: { number },
      };
    }
    if (!member.relationship.trim()) {
      return {
        messageKey: vkey("familyMemberRelationshipRequired"),
        params: { number },
      };
    }
    if (!member.mobile.trim()) {
      return {
        messageKey: vkey("familyMemberMobileRequired"),
        params: { number },
      };
    }
    if (!isValidMobileNumber(member.mobile, member.mobileCountry)) {
      return {
        messageKey: vkey("familyMemberMobileInvalid"),
        params: { number },
      };
    }
    if (!member.details.trim()) {
      return {
        messageKey: vkey("familyMemberDetailsRequired"),
        params: { number },
      };
    }
  }
  for (let i = REQUIRED_FAMILY_MEMBER_COUNT; i < familyMembers.length; i++) {
    const member = familyMembers[i];
    if (
      member?.mobile.trim() &&
      !isValidMobileNumber(member.mobile, member.mobileCountry)
    ) {
      return {
        messageKey: vkey("familyMemberMobileInvalid"),
        params: { number: i + 1 },
      };
    }
  }
  return null;
}

// Validate only the fields that belong to a single step. Returns an i18n
// message key for the first invalid field on that step, or null when valid.
export function validateInquiryStep(
  step: number,
  form: AdmissionInquiryFormState,
  location: { districtId: string; cityId: string; areaId: string },
  elderLocation: { districtId: string; cityId: string; areaId: string },
  familyMembers: InquiryFamilyMember[]
): InquiryValidationError | null {
  if (step === 0) {
    if (!form.applicationDate) {
      return { messageKey: vkey("applicationDateRequired") };
    }
    if (!form.inquirerName.trim()) {
      return { messageKey: vkey("inquirerNameRequired") };
    }
    const contactError = validateInquirerContact(form);
    if (contactError) {
      return contactError;
    }
    const referenceError = validateReferencePersonContact(form);
    if (referenceError) {
      return referenceError;
    }
    if (!location.districtId) {
      return { messageKey: vkey("districtRequired") };
    }
    if (!location.cityId) {
      return { messageKey: vkey("cityRequired") };
    }
    if (!location.areaId) {
      return { messageKey: vkey("areaRequired") };
    }
    if (!form.pincode.trim() || !/^[0-9]{6}$/.test(form.pincode.trim())) {
      return { messageKey: vkey("pincodeRequired") };
    }
  }

  if (step === 1) {
    return validateRequiredFamilyMembers(familyMembers);
  }

  if (step === 2) {
    if (!form.elderName.trim()) {
      return { messageKey: vkey("elderNameRequired") };
    }
    const weightError = validateElderWeight(form);
    if (weightError) {
      return weightError;
    }
    if (!elderLocation.districtId) {
      return { messageKey: vkey("elderDistrictRequired") };
    }
    if (!elderLocation.cityId) {
      return { messageKey: vkey("elderCityRequired") };
    }
    if (!elderLocation.areaId) {
      return { messageKey: vkey("elderAreaRequired") };
    }
    if (
      !form.elderPincode.trim() ||
      !/^[0-9]{6}$/.test(form.elderPincode.trim())
    ) {
      return { messageKey: vkey("elderPincodeRequired") };
    }
    if (!form.reasonForAdmission.trim()) {
      return { messageKey: vkey("reasonRequired") };
    }
  }

  if (step === 3) {
    if (!form.otherIncome.trim()) {
      return { messageKey: vkey("otherIncomeRequired") };
    }
  }

  return null;
}

export function validateInquiryForm(
  form: AdmissionInquiryFormState,
  location: { districtId: string; cityId: string; areaId: string },
  elderLocation: { districtId: string; cityId: string; areaId: string },
  familyMembers: InquiryFamilyMember[]
): {
  step: number;
  messageKey: string;
  params?: Record<string, string | number>;
} | null {
  if (!form.applicationDate) {
    return { step: 0, messageKey: vkey("applicationDateRequired") };
  }
  if (!form.inquirerName.trim()) {
    return { step: 0, messageKey: vkey("inquirerNameRequired") };
  }
  const contactError = validateInquirerContact(form);
  if (contactError) {
    return { step: 0, ...contactError };
  }
  const referenceError = validateReferencePersonContact(form);
  if (referenceError) {
    return { step: 0, ...referenceError };
  }
  if (!location.districtId) {
    return { step: 0, messageKey: vkey("districtRequired") };
  }
  if (!location.cityId) {
    return { step: 0, messageKey: vkey("cityRequired") };
  }
  if (!location.areaId) {
    return { step: 0, messageKey: vkey("areaRequired") };
  }
  if (!form.pincode.trim() || !/^[0-9]{6}$/.test(form.pincode.trim())) {
    return { step: 0, messageKey: vkey("pincodeRequired") };
  }
  const familyError = validateRequiredFamilyMembers(familyMembers);
  if (familyError) {
    return { step: 1, ...familyError };
  }
  if (!form.elderName.trim()) {
    return { step: 2, messageKey: vkey("elderNameRequired") };
  }
  const weightError = validateElderWeight(form);
  if (weightError) {
    return { step: 2, ...weightError };
  }
  if (!elderLocation.districtId) {
    return { step: 2, messageKey: vkey("elderDistrictRequired") };
  }
  if (!elderLocation.cityId) {
    return { step: 2, messageKey: vkey("elderCityRequired") };
  }
  if (!elderLocation.areaId) {
    return { step: 2, messageKey: vkey("elderAreaRequired") };
  }
  if (
    !form.elderPincode.trim() ||
    !/^[0-9]{6}$/.test(form.elderPincode.trim())
  ) {
    return { step: 2, messageKey: vkey("elderPincodeRequired") };
  }
  if (!form.reasonForAdmission.trim()) {
    return { step: 2, messageKey: vkey("reasonRequired") };
  }
  if (!form.otherIncome.trim()) {
    return { step: 3, messageKey: vkey("otherIncomeRequired") };
  }
  return null;
}
